import { defineStyle } from "@chakra-ui/styled-system"
import { theme } from "@chakra-ui/theme"
import type { StyleFunctionProps } from "@chakra-ui/theme-tools"

const variantSolidRounded = defineStyle((props: StyleFunctionProps) => {
  // @ts-expect-error expression not callable
  const original = theme.components.Tabs.variants["solid-rounded"](props)

  return {
    ...original,
    tab: {
      ...original.tab,
      _selected: {
        ...original.tab._selected,
        bg: theme.components.Button.variants?.solid?.(props)?.bg,
        color: props.colorScheme === "tertiary" ? "neutrals.800" : "#ffffff",
      },
    },
  }
})

const variantSemiRounded = defineStyle((props: StyleFunctionProps) => {
  // @ts-expect-error expression not callable
  const original = theme.components.Tabs.variants["solid-rounded"](props)
  const { colorScheme } = props

  return {
    ...original,
    tab: {
      ...original.tab,
      fontSize: { base: 10, md: 12, lg: 14 },
      borderRadius: "0.25rem",
      ...(colorScheme === "tertiary" && {
        bg: "white",
      }),
      _selected: {
        ...original.tab._selected,
        bg:
          colorScheme === "tertiary"
            ? `${colorScheme}.500`
            : theme.components.Button.variants?.solid?.(props)?.bg,
        color: props.colorScheme === "tertiary" ? "neutrals.800" : "#ffffff",
      },
    },
  }
})

const variantElevated = defineStyle((props: StyleFunctionProps) => {
  // @ts-expect-error expression not callable
  const original = theme.components.Tabs.variants["solid-rounded"](props)
  const { colorScheme } = props

  const baseStyle = {
    tablist: {
      p: 2,
      borderRadius: "0.75rem",
      bg: "neutrals.100",
    },
    tab: {
      ...original.tab,
      py: 3.5,
      fontSize: { base: 10, md: 12, lg: 14 },
      borderRadius: "0.5rem",
      color: "neutrals.800",
      bg: "transparent",
      _selected: {
        boxShadow: "0 5px 5px 0 var(--chakra-colors-blackAlpha-100)",
        bg: "white",
      },
    },
  }

  switch (colorScheme) {
    case "secondary": {
      return {
        tablist: {
          ...baseStyle.tablist,
          bg: "neutrals.200",
        },
        tab: {
          ...baseStyle.tab,
          color: "neutrals.800",
          bg: "transparent",
          _selected: {
            color: "white",
            boxShadow: "0 5px 5px 0 var(--chakra-colors-blackAlpha-100)",
            bg: "neutrals.800",
          },
        },
      }
    }
    default: {
      return baseStyle
    }
  }
})

const variantPils = defineStyle((props: StyleFunctionProps) => {
  const { colorScheme } = props

  const baseStyle = {
    tablist: {
      p: 1,
      bg: "white",
      border: "1px solid",
      borderColor: "main.100",
      borderRadius: "full",
    },
    tab: {
      py: "0.3125rem",
      px: { base: 4, md: 8 },
      lineHeight: 1.71429,
      fontSize: { base: 12, md: 14 },
      fontWeight: 500,
      borderRadius: "full",
      color: "black",
      bg: "transparent",
      _selected: {
        bg: "main.100",
      },
    },
  }

  switch (colorScheme) {
    case "secondary": {
      return {
        tablist: {
          ...baseStyle.tablist,
          bg: "neutrals.200",
          borderColor: "neutrals.200",
        },
        tab: {
          ...baseStyle.tab,
          color: "neutrals.800",
          bg: "transparent",
          _selected: {
            color: "white",
            bg: "neutrals.800",
          },
        },
      }
    }
    default: {
      return baseStyle
    }
  }
})

export const Tabs = {
  baseStyle: {
    tab: {
      bg: "neutrals.100",
    },
  },
  variants: {
    "solid-rounded": variantSolidRounded,
    "semi-rounded": variantSemiRounded,
    elevated: variantElevated,
    pils: variantPils,
  },
  sizes: {
    sm: {
      tab: {
        py: 1,
        px: 4,
        fontSize: 12,
      },
    },
    md: {
      tab: {
        fontSize: 14,
        py: 2,
        px: 4,
      },
    },
    lg: {
      tab: {
        fontSize: 16,
        py: 3,
        px: 4,
      },
    },
  },
  defaultProps: {
    variant: "semi-rounded",
    colorScheme: "primary",
  },
}
